// Entrées de navigation de l'espace Administration (charte « Séance de décision ») : entrées
// principales de l'en-tête (Décisions, Pilotage, Courriers, Dossiers, Finances, Territoire,
// Conseil) et entrées du menu « Plus ». Filtrées par les droits du contexte actif et par les
// modules activés (H9-01 : un module désactivé masque ses écrans de navigation). Une entrée
// n'apparaît que si son écran existe réellement.
import type { EntreeNavigation } from '@cc/ui';
import {
  CalendarDays,
  Coins,
  FileCog,
  FileText,
  FolderOpen,
  Gavel,
  History,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  Map as IconeCarte,
  Mail,
  MessageSquareWarning,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Handshake,
  Store,
  Users,
  Wrench,
  Recycle,
  ClipboardCheck,
  Landmark,
  ShoppingCart,
  HardHat,
  Building2, CalendarRange, Vote,
  Contact,
  MapPinned, PartyPopper, // Lot L5-C1.
} from 'lucide-react';
import type { ContexteActif, ModuleEtat } from '../api/types';
import { peut, rolesActifs, voitDomaine } from '../session/permissions';

interface DefinitionEntree extends Omit<EntreeNavigation, 'badge'> {
  visible: (c: ContexteActif | null) => boolean;
  module?: string;
  /** Entrée du menu « Plus » plutôt que de l'en-tête. */
  secondaire?: boolean;
}

/** Le super administrateur de la plateforme (D-080) ne voit que ses écrans et ses préférences. */
const horsPlateforme = (c: ContexteActif | null) => c?.appartenance_active?.type !== 'institutionnel'; // Lot L6-A : ni plateforme ni supervision.

const DEFINITIONS: DefinitionEntree[] = [
  { id: 'decisions', libelle: 'Décisions', href: '/decisions', icone: Gavel, visible: (c) => voitDomaine(c, 'dossier') || voitDomaine(c, 'courrier'), module: 'M02' },
  { id: 'pilotage', libelle: 'Pilotage', href: '/', icone: LayoutDashboard, visible: horsPlateforme },
  { id: 'courriers', libelle: 'Courriers', href: '/courriers', icone: Mail, visible: (c) => voitDomaine(c, 'courrier'), module: 'M02' },
  { id: 'dossiers', libelle: 'Dossiers', href: '/dossiers', icone: FolderOpen, visible: (c) => voitDomaine(c, 'dossier'), module: 'M02' },
  // Finances (lot L2-C) : recettes, créances, paiements, caisses, rapprochements, anomalies, catalogue.
  { id: 'finances', libelle: 'Finances', href: '/recettes', icone: Coins, visible: (c) => voitDomaine(c, 'recette') || voitDomaine(c, 'caisse'), module: 'M07' },
  // Budget et chaîne de la dépense (lot L4-B, M06) : domaines budget et depense.
  { id: 'budget', libelle: 'Budget', href: '/budget', icone: Landmark, visible: (c) => voitDomaine(c, 'budget') || voitDomaine(c, 'depense'), module: 'M06' },
  { id: 'territoire', libelle: 'Territoire', href: '/territoire', icone: IconeCarte, visible: horsPlateforme },
  // Marchés municipaux et domaine public (lot L2) : visibles avec un droit sur l'un des deux domaines.
  { id: 'marches', libelle: 'Marchés', href: '/marches', icone: Store, visible: (c) => voitDomaine(c, 'marche_municipal') || voitDomaine(c, 'domaine_public') },
  // Super administrateur de la plateforme (D-080) : catalogue et activation globale des modules.
  { id: 'plateforme', libelle: 'Plateforme', href: '/plateforme/modules', icone: LayoutGrid, visible: (c) => c?.appartenance_active?.institution === 'plateforme' },
  { id: 'supervision', libelle: 'Supervision', href: '/supervision', icone: IconeCarte, visible: (c) => c?.appartenance_active?.type === 'institutionnel' && c.appartenance_active.institution !== 'plateforme' }, // Lot L6-A : supervision nationale (M24).
  // Interventions (lot L3-A) : signalements, missions, équipes, conflits de synchronisation (module M12).
  { id: 'interventions', libelle: 'Interventions', href: '/interventions', icone: Wrench, visible: (c) => voitDomaine(c, 'intervention'), module: 'M12' },
  // Lot L3-B : propreté (M13, domaine dechets) et contrôles (M23, domaine inspection), dans le menu Plus.
  { id: 'proprete', libelle: 'Propreté', href: '/proprete', icone: Recycle, visible: (c) => voitDomaine(c, 'dechets'), module: 'M13', secondaire: true },
  { id: 'controles', libelle: 'Contrôles', href: '/inspections', icone: ClipboardCheck, visible: (c) => voitDomaine(c, 'inspection'), module: 'M23', secondaire: true },
  // Lot L4-C : marchés publics et fournisseurs (M15, domaine achat), travaux et chantiers (M14, domaine travaux), dans le menu Plus.
  { id: 'achats', libelle: 'Achats', href: '/achats', icone: ShoppingCart, visible: (c) => voitDomaine(c, 'achat'), module: 'M15', secondaire: true },
  { id: 'travaux', libelle: 'Travaux', href: '/travaux', icone: HardHat, visible: (c) => voitDomaine(c, 'travaux'), module: 'M14', secondaire: true },
  // Lot L4-A : patrimoine (M10, domaine patrimoine) et équipements publics (M19, domaine equipement), dans le menu Plus.
  { id: 'patrimoine', libelle: 'Patrimoine', href: '/patrimoine', icone: Building2, visible: (c) => voitDomaine(c, 'patrimoine'), module: 'M10', secondaire: true },
  { id: 'equipements', libelle: 'Équipements', href: '/equipements', icone: CalendarRange, visible: (c) => voitDomaine(c, 'equipement'), module: 'M19', secondaire: true },
  { id: 'personnel', libelle: 'Personnel', href: '/personnel', icone: Contact, visible: (c) => voitDomaine(c, 'personnel') || c?.appartenance_active?.type === 'local', module: 'M11', secondaire: true }, // Lot L5-A : personnel sans paie (M11) ; tout agent y trouve son espace.
  // Application terrain (lot L3-C) : agents de terrain et responsables techniques ; zone /terrain hors de la coquille.
  { id: 'terrain', libelle: 'Terrain', href: '/terrain', icone: Smartphone, visible: (c) => rolesActifs(c).some((r) => r === 'agent_terrain' || r === 'responsable_technique') },
  // Conseil (lot L5-B, M16, domaine assemblee) : sessions, élus et règles, délibérations.
  { id: 'conseil', libelle: 'Conseil', href: '/conseil', icone: Vote, visible: (c) => voitDomaine(c, 'assemblee'), module: 'M16' },
  { id: 'proximite', libelle: 'Proximité', href: '/proximite', icone: MapPinned, visible: (c) => voitDomaine(c, 'quartier'), module: 'M17', secondaire: true }, // Lot L5-C1 : quartiers et participation.
  { id: 'evenements', libelle: 'Événements', href: '/evenements', icone: PartyPopper, visible: (c) => voitDomaine(c, 'evenement'), module: 'M21', secondaire: true }, // Lot L5-C1 : événements et vie locale.
  { id: 'urbanisme', libelle: 'Urbanisme', href: '/urbanisme', icone: IconeCarte, visible: (c) => voitDomaine(c, 'urbanisme'), module: 'M18', secondaire: true }, // Lot L5-C2 : urbanisme et aménagement (M18).
  { id: 'documents', libelle: 'Documents', href: '/documents', icone: FileText, visible: (c) => voitDomaine(c, 'document'), module: 'M03', secondaire: true },
  { id: 'demarches', libelle: 'Démarches', href: '/demarches', icone: FileCog, visible: (c) => voitDomaine(c, 'demarche_config') || voitDomaine(c, 'dossier'), module: 'M05', secondaire: true },
  { id: 'rendez-vous', libelle: 'Rendez-vous', href: '/rendez-vous', icone: CalendarDays, visible: (c) => voitDomaine(c, 'dossier'), module: 'M05', secondaire: true },
  { id: 'reclamations', libelle: 'Réclamations', href: '/reclamations', icone: MessageSquareWarning, visible: (c) => voitDomaine(c, 'reclamation') || voitDomaine(c, 'reclamation_sensible'), module: 'M22', secondaire: true },
  { id: 'recherche', libelle: 'Recherche', href: '/recherche', icone: Search, visible: horsPlateforme, secondaire: true },
  { id: 'utilisateurs', libelle: 'Utilisateurs', href: '/administration/utilisateurs', icone: Users, visible: (c) => peut(c, 'utilisateurs', 'voir'), secondaire: true },
  { id: 'roles', libelle: 'Rôles', href: '/administration/roles', icone: ShieldCheck, visible: (c) => peut(c, 'utilisateurs', 'voir'), secondaire: true },
  {
    id: 'habilitations',
    libelle: 'Habilitations',
    href: '/administration/habilitations',
    icone: KeyRound,
    visible: (c) => peut(c, 'utilisateurs', 'administrer') || peut(c, 'utilisateurs', 'valider'),
    secondaire: true,
  },
  { id: 'delegations', libelle: 'Délégations', href: '/administration/delegations', icone: Handshake, visible: (c) => Boolean(c?.appartenance_active) && horsPlateforme(c), secondaire: true },
  { id: 'parametres', libelle: 'Paramètres', href: '/administration/parametres', icone: Settings, visible: (c) => peut(c, 'parametres', 'voir'), secondaire: true },
  { id: 'modules', libelle: 'Modules', href: '/administration/modules', icone: LayoutGrid, visible: (c) => peut(c, 'parametres', 'voir'), secondaire: true },
  { id: 'historique', libelle: 'Historique', href: '/administration/historique', icone: History, visible: (c) => peut(c, 'audit', 'voir'), secondaire: true },
  { id: 'preferences', libelle: 'Préférences', href: '/preferences', icone: SlidersHorizontal, visible: () => true, secondaire: true },
];

export interface Navigation {
  principales: EntreeNavigation[];
  secondaires: EntreeNavigation[];
}

export function entreesNavigation(
  contexte: ContexteActif | null,
  modules: ModuleEtat[] | undefined,
  badges: Record<string, { nombre: number; libelle: string } | undefined> = {},
): Navigation {
  // Liste des modules (administrateurs) ou, pour tout agent, les modules actifs annoncés par GET /contexte.
  const annonces = contexte?.appartenance_active?.modules_actifs;
  const actifs = modules ? new Set(modules.filter((m) => m.actif).map((m) => m.module)) : annonces ? new Set(annonces) : null;
  const visibles = DEFINITIONS.filter((d) => d.visible(contexte) && (!d.module || !actifs || actifs.has(d.module)));
  const vers = ({ visible: _v, module: _m, secondaire: _s, ...e }: DefinitionEntree): EntreeNavigation => ({
    ...e,
    badge: badges[e.id]?.nombre ?? null,
    libelleBadge: badges[e.id]?.libelle,
  });
  return { principales: visibles.filter((d) => !d.secondaire).map(vers), secondaires: visibles.filter((d) => d.secondaire).map(vers) };
}

const PRIORITE_RAPIDES = ['decisions', 'pilotage', 'courriers', 'dossiers', 'recherche', 'utilisateurs', 'parametres', 'preferences'];

/** Quatre entrées de la navigation basse (téléphone). */
export function entreesRapides(navigation: Navigation): string[] {
  const ids = new Set([...navigation.principales, ...navigation.secondaires].map((e) => e.id));
  return PRIORITE_RAPIDES.filter((id) => ids.has(id)).slice(0, 4);
}

/** Entrée active d'après le chemin. */
export function entreeActive(chemin: string): string {
  if (chemin === '/' || chemin.startsWith('/tableau-de-bord')) return 'pilotage';
  const correspondance = DEFINITIONS.filter((d) => d.href !== '/').find((d) => chemin === d.href || chemin.startsWith(`${d.href}/`));
  return correspondance?.id ?? '';
}
